// The canvas: a project's story drawn natively on each screen. The sales manager's view and the customer's are the
// same drawing, fed the same small state — which scene, which unit, which room, where the camera looks — so a floor
// plan is sharp on any phone and nothing but that state travels.
import { pickLang } from "./i18n.js";
import { panoIn } from "./pano.js";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
// Facts in canvas.json may carry <b> for emphasis and nothing else.
const rich = (s) => esc(s).replace(/&lt;b&gt;/g, "<b>").replace(/&lt;\/b&gt;/g, "</b>");

export async function loadCanvas(id) {
  // Always checked with the server (a 304 when unchanged): a browser that kept an older copy still gets today's canvas.
  const res = await fetch(`/canvas/${encodeURIComponent(id)}/canvas.json`, { credentials: "same-origin", cache: "no-cache" });
  if (!res.ok) throw new Error(`canvas ${id}: ${res.status}`);
  const canvas = await res.json();
  canvas.base = `/canvas/${encodeURIComponent(id)}/`;
  canvas.byId = Object.fromEntries(canvas.scenes.map((s) => [s.id, s]));
  // A 360° walk-through's viewpoints, arrows and plan come with the canvas (scripts/import-panom.mjs).
  await Promise.all(canvas.scenes.filter((s) => s.kind === "pano" && s.data).map(async (s) => {
    const r = await fetch(canvas.base + s.data, { credentials: "same-origin", cache: "no-cache" }).catch(() => null);
    s.pano = r && r.ok ? await r.json() : null;
  }));
  return canvas;
}

export function sceneOf(canvas, id) {
  return canvas.byId[id] || pageScene(canvas, id) || canvas.scenes[0];
}

// Pages the sales manager adds during the meeting (W3): a blank board, or a still frozen from a tab he shared. They are
// not in canvas.json; every screen makes them from the id alone ("board:2", "still:1") and, for a still, the picture
// it was sent (canvas.stills: n → { src, w, h }).
export const isBoard = (id) => /^board:[1-9]\d?$/.test(String(id));
export const isStill = (id) => /^still:[1-9]\d?$/.test(String(id));
export const isPage = (id) => isBoard(id) || isStill(id);
const BOARD = { w: 1000, h: 750 };
function pageScene(canvas, id) {
  const n = +String(id).split(":")[1];
  if (isBoard(id)) {
    const name = n === 1 ? "The board" : `The board · ${n}`;
    return { id, kind: "board", name: { en: name, hi: name, kn: name }, w: BOARD.w, h: BOARD.h };
  }
  if (isStill(id)) {
    const got = canvas.stills?.get(n);
    const name = `A still · ${n}`;
    return { id, kind: "still", name: { en: name, hi: name, kn: name }, w: got?.w || 1600, h: got?.h || 1000, src: got?.src || "" };
  }
  return null;
}
// A scene's name wherever one is shown, the pages added in the meeting included.
export function sceneName(canvas, id) {
  const sc = canvas?.byId?.[id] || (canvas && pageScene(canvas, id));
  return sc ? sc.name.en : id;
}

export function variantOf(scene, view) {
  if (scene.kind !== "plan") return null;
  return scene.variants[view.variant] || Object.values(scene.variants)[0];
}

export function roomOf(scene, view) {
  if (scene.kind !== "tour" && scene.kind !== "embed") return null;
  return scene.rooms.find((r) => r.id === view.room) || scene.rooms[0];
}

// A walk-through someone else hosts — Panom's 360° showcase — drawn in a frame on both screens. The pilot leads room by
// room: each room is one of the showcase's viewpoints, named in its address. Only the hosts below may be framed (the
// page's Content-Security-Policy says the same); anything else draws nothing.
export const EMBED_ORIGINS = ["https://app.panom.ai"];
export function embedSrc(scene, room) {
  let u;
  try { u = new URL(scene.src); } catch { return "about:blank"; }
  if (!EMBED_ORIGINS.includes(u.origin)) return "about:blank";
  if (room && room.frame) u.searchParams.set("frameId", room.frame);
  return u.href;
}

// A film the sales manager plays for both screens: from the canvas's own folder, or from ClikHome's media.
export const MEDIA_ORIGINS = ["https://clikhome.in", "https://mana.clikhome.in"];
export function videoSrc(scene, base) {
  const src = String(scene.src || "");
  if (/^[a-z0-9][\w./-]*$/i.test(src) && !src.includes("..")) return base + src;
  try { const u = new URL(src); return MEDIA_ORIGINS.includes(u.origin) ? u.href : ""; } catch { return ""; }
}

// Where the project is, one tap from Google Maps on either screen.
const fmtLL = (m) => `${Math.abs(m.lat).toFixed(5)}° ${m.lat >= 0 ? "N" : "S"}, ${Math.abs(m.lng).toFixed(5)}° ${m.lng >= 0 ? "E" : "W"}`;
export function mapLinks(m) {
  const q = encodeURIComponent(`${m.lat},${m.lng}`);
  return { open: `https://www.google.com/maps/search/?api=1&query=${q}`, directions: `https://www.google.com/maps/dir/?api=1&destination=${q}` };
}
function mapCard(m) {
  if (!m || !Number.isFinite(m.lat) || !Number.isFinite(m.lng)) return "";
  const l = mapLinks(m);
  return `<div class="cv-map"><b>${esc(m.label || "")}</b>${m.address ? `<small>${esc(m.address)}</small>` : ""}<span class="ll">${fmtLL(m)}</span>
    <div class="go"><a href="${esc(l.open)}" target="_blank" rel="noopener" data-map="open">Open in Google Maps</a><a href="${esc(l.directions)}" target="_blank" rel="noopener" data-map="directions">Directions</a></div></div>`;
}

export function spotsOf(scene, view) {
  if (scene.kind === "grid") return scene.items.map((i) => ({ id: i.id, label: i.t }));
  if (scene.kind === "plan") return variantOf(scene, view).spots || [];
  return scene.spots || [];
}

export function thumbOf(canvas, scene, view = {}) {
  const img = scene.kind === "plan" ? variantOf(scene, view).img
    : scene.kind === "tour" ? roomOf(scene, view).img
    : scene.kind === "grid" ? scene.items[0].img
    : scene.kind === "pano" && scene.pano ? (scene.pano.frames[view.room] || scene.pano.frames[scene.pano.start]).sm
    : scene.kind === "board" ? null
    : scene.kind === "still" ? null
    : scene.img || canvas.hero;
  if (scene.kind === "still") return scene.src || "";
  return img ? canvas.base + img : "";
}

// ctx: { who: "nav" | "cust", lang, first (customer's first name), booking, slots }
export function canvasHTML(canvas, view, ctx) {
  const sc = sceneOf(canvas, view.scene);
  const B = canvas.base;
  if (sc.kind === "cover") {
    const title = sc.title ? `<div class="cv-title"><p class="k">${esc(canvas.brand)}</p><p class="t">${esc(pickLang(sc.title, ctx.lang).replace("{first}", ctx.first || ""))}</p><p class="s">${esc(sc.sub || "")}</p></div>` : "";
    const facts = sc.facts ? `<div class="cv-facts">${sc.facts.map((f) => `<span>${rich(f)}</span>`).join("")}</div>` : "";
    return `<div class="cv dark"><div class="cv-cover"><img src="${B}${esc(sc.img)}" style="object-position:${esc(sc.pos || "50% 50%")}" alt=""></div>${title}${facts}</div>`;
  }
  // A blank board: a page of paper, the same ink layer as a plan. A still: the frame he froze, drawn from the picture sent.
  if (sc.kind === "board" || sc.kind === "still") {
    const ink = `<svg class="ink" viewBox="0 0 ${+sc.w} ${+sc.h}" preserveAspectRatio="none" aria-hidden="true"></svg><div class="ink-lbls"></div>`;
    const face = sc.kind === "board" ? `<div class="paper"></div>` : sc.src ? `<img src="${esc(sc.src)}" alt="" draggable="false">` : `<div class="paper still-wait"><span>The still is arriving…</span></div>`;
    return `<div class="cv ${sc.kind}"><div class="cv-center"><div class="cv-fit" data-w="${+sc.w}" data-h="${+sc.h}">${face}${ink}<div class="ptr" hidden><span>${esc(ctx.hostFirst || "")}</span></div></div></div></div>`;
  }
  if (sc.kind === "fit" || sc.kind === "plan") {
    const v = sc.kind === "plan" ? variantOf(sc, view) : sc;
    const spots = (v.spots || []).map((p) => `<div class="hs${view.spot === p.id ? " on" : ""}" data-spot="${esc(p.id)}" style="left:${+p.x}%;top:${+p.y}%"><i></i><em>${esc(p.label)}</em></div>`).join("");
    const stats = sc.stats ? `<div class="cv-stats">${sc.stats.map(([b, s]) => `<div><b>${esc(b)}</b><small>${esc(s)}</small></div>`).join("")}</div>` : "";
    const facts = sc.kind === "plan" ? `<div class="cv-facts"><span><b>${esc(v.name)}</b></span>${v.facts.map((f) => `<span>${rich(f)}</span>`).join("")}</div>` : "";
    // The ink layer is the page's own size, so a mark is drawn in the plan's units and moves with every zoom (ink.js).
    const ink = `<svg class="ink" viewBox="0 0 ${+v.w} ${+v.h}" preserveAspectRatio="none" aria-hidden="true"></svg><div class="ink-lbls"></div>`;
    return `<div class="cv" style="background:${esc(v.bg || sc.bg || "#fff")}"><div class="cv-center"><div class="cv-fit" data-w="${+v.w}" data-h="${+v.h}"><img src="${B}${esc(v.img)}" alt="" draggable="false">${ink}${spots}<div class="ptr" hidden><span>${esc(ctx.hostFirst || "")}</span></div></div></div>${facts}${stats}${mapCard(sc.map)}</div>`;
  }
  if (sc.kind === "tour") {
    const room = roomOf(sc, view);
    const yaw = view.yaw ?? 40;
    return `<div class="cv dark tour"><div class="pano" style="background-image:url('${B}${esc(room.img)}');background-position:${yaw}% 50%"></div>
      <div class="tour-where"><b>${esc(room.label)}</b></div>
      ${canvas.minimap ? `<div class="minimap"><img src="${B}${esc(canvas.minimap)}" alt=""><i class="you" style="left:${+room.x}%;top:${+room.y}%"><b style="transform:rotate(${(yaw - 50) * 1.8}deg)"></b></i></div>` : ""}
      <div class="tour-rooms">${sc.rooms.map((r) => `<span class="${r.id === room.id ? "on" : ""}">${esc(r.label)}</span>`).join("")}</div>
      <div class="tour-hint">${esc(ctx.who === "nav" ? "Drag to turn — their view turns with yours" : ctx.lookAround || "Drag to look around")}</div></div>`;
  }
  if (sc.kind === "embed") {
    const room = roomOf(sc, view);
    return `<div class="cv dark embed"><iframe class="embed-frame" src="${esc(embedSrc(sc, room))}" title="${esc(sc.unit || "The 360° walk-through")}"
      allow="fullscreen; accelerometer; gyroscope; xr-spatial-tracking" allowfullscreen referrerpolicy="strict-origin-when-cross-origin" data-room="${esc(room.id)}"></iframe></div>`;
  }
  if (sc.kind === "video") {
    const src = videoSrc(sc, B);
    return `<div class="cv dark vid">${src ? `<video class="cv-video" playsinline preload="metadata"${sc.poster ? ` poster="${B}${esc(sc.poster)}"` : ""} src="${esc(src)}"></video>` : ""}
      <button class="vid-sound" type="button" hidden>${esc(ctx.who === "nav" ? "" : ctx.tapSound || "Tap for sound")}</button></div>`;
  }
  if (sc.kind === "pano") {
    return `<div class="cv dark">${sc.pano ? `<div class="pano360"></div>` : `<div class="pano-note">The 360° walk-through has not been brought in yet.</div>`}</div>`;
  }
  if (sc.kind === "grid") {
    return `<div class="cv-grid">${sc.items.map((it) => `<figure data-spot="${esc(it.id)}" class="${view.spot === it.id ? "on" : ""}"><img src="${B}${esc(it.img)}" alt=""><figcaption>${esc(it.t)}<small>${esc(it.s)}</small></figcaption></figure>`).join("")}</div>`;
  }
  if (sc.kind === "price") {
    const unit = ctx.unit || "home";
    return `<div class="cv-doc"><h4>Price &amp; payment plan</h4><p class="sub">${esc(sc.planTitle || "")}</p>
      <table class="pp">${sc.plan.map(([a, b], i) => `<tr class="${i === 0 ? "hl" : ""}"><td>${esc(a)}</td><td>${esc(b)}</td></tr>`).join("")}<tr class="tot"><td>Total</td><td>100%</td></tr></table>
      <div class="lock"><b>The price of your ${esc(unit)}</b>${esc(ctx.hostFirst || "Your sales manager")} shares it from Mana's cost sheet, for the exact unit and floor — on WhatsApp, so you have it after the call.</div>
      <div class="ptr" hidden><span>${esc(ctx.hostFirst || "")}</span></div></div>`;
  }
  if (sc.kind === "next") {
    const b = ctx.booking;
    return `<div class="cv-next"><h4>See it in person</h4><p>Stand in the home you just saw. A car can collect you, and the visit takes about an hour.</p>
      <div class="slots">${(ctx.slots || []).map((s) => `<div class="slot${b && b.at === s.at ? " on" : ""}"><b>${esc(s.time)}</b>${esc(s.day)}</div>`).join("")}</div>
      ${b ? `<div class="booked">✓ Booked — ${esc(b.day)}, ${esc(b.time)}.</div>` : ""}</div>`;
  }
  return `<div class="cv"></div>`;
}

// Size a plan to its box like object-fit: contain, so hotspots and the pointer stay in the image's own units.
export function fitPlans(root) {
  for (const f of root.querySelectorAll(".cv-fit")) {
    const box = f.parentElement;
    const W = box.clientWidth, H = box.clientHeight, w = +f.dataset.w, h = +f.dataset.h;
    if (!W || !H || !w || !h) continue;
    const k = Math.min(W / w, H / h) * 0.96;
    f.style.width = `${w * k}px`;
    f.style.height = `${h * k}px`;
  }
}

// The camera on a plan: the sales manager's spotlight flies both screens to a room (the zoom is anchored there);
// an exploring customer has her own pan and pinch.
export function applyView(root, canvas, view, own = null) {
  const f = root.querySelector(".cv-fit");
  const sc = sceneOf(canvas, view.scene);
  if (f) {
    const spot = spotsOf(sc, view).find((p) => p.id === view.spot);
    if (own) {
      f.style.transformOrigin = "50% 50%";
      f.style.transform = `translate(${own.tx || 0}px, ${own.ty || 0}px) scale(${own.zoom || 1})`;
    } else {
      f.style.transformOrigin = spot && spot.x != null ? `${spot.x}% ${spot.y}%` : "50% 50%";
      f.style.transform = `scale(${view.zoom || 1})`;
    }
    for (const hs of f.querySelectorAll(".hs")) hs.classList.toggle("on", hs.dataset.spot === view.spot);
  }
  for (const fig of root.querySelectorAll(".cv-grid figure")) fig.classList.toggle("on", fig.dataset.spot === view.spot);
  const pano = root.querySelector(".pano");
  if (pano) paintYaw(root, view.yaw ?? 40);
  // The 360° walk-through: following glides to the sales manager's view; one's own view is where one left it.
  const pv = sc.kind === "pano" && panoIn(root);
  if (pv) pv.show({ frame: (own || view).room, yaw: (own || view).yaw ?? 0, pitch: (own || view).pitch ?? 0, zoom: view.zoom ?? 1 }, { animate: !own });
}

export function paintYaw(root, yaw) {
  const pano = root.querySelector(".pano");
  if (pano) pano.style.backgroundPosition = `${yaw}% 50%`;
  const cone = root.querySelector(".minimap b");
  if (cone) cone.style.transform = `rotate(${(yaw - 50) * 1.8}deg)`;
}

// The price page reflows between a laptop and a phone, so a point on it is kept as a place in one of its rows (or the
// note under the table): the row's number and where in it — the same words on every screen (W3).
const ANCHORS = ".pp tr, .lock";
export function docAnchor(doc, clientX, clientY) {
  const rows = doc ? [...doc.querySelectorAll(ANCHORS)] : [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i].getBoundingClientRect();
    if (clientY >= r.top && clientY <= r.bottom && clientX >= r.left && clientX <= r.right) {
      return { row: i, fx: Math.round(((clientX - r.left) / r.width) * 1000) / 1000, fy: Math.round(((clientY - r.top) / r.height) * 1000) / 1000 };
    }
  }
  return null;
}
export function docSpot(doc, a) {
  const row = a && doc ? doc.querySelectorAll(ANCHORS)[a.row] : null;
  if (!row) return null;
  const d = doc.getBoundingClientRect(), r = row.getBoundingClientRect();
  return { left: r.left - d.left + doc.scrollLeft + r.width * a.fx, top: r.top - d.top + doc.scrollTop + r.height * a.fy, text: row.querySelector("td")?.textContent || row.querySelector("b")?.textContent || "" };
}
export function paintPointer(root, ptr) {
  const pv = panoIn(root);
  if (pv) { pv.pointer(ptr && Number.isFinite(ptr.u) ? ptr : null); return; }
  const doc = root.querySelector(".cv-doc");
  if (doc) {
    const p = doc.querySelector(":scope > .ptr");
    const at = ptr && Number.isFinite(ptr.row) ? docSpot(doc, ptr) : null;
    if (p) { p.hidden = !at; if (at) { p.style.left = `${at.left}px`; p.style.top = `${at.top}px`; } }
    return;
  }
  const p = root.querySelector(".ptr");
  if (!p) return;
  if (!ptr) { p.hidden = true; return; }
  p.hidden = false;
  p.style.left = `${ptr.x}%`;
  p.style.top = `${ptr.y}%`;
}

// Visit slots: one list for her screen and for the back end that checks her booking (lib/slots.js).
export { openSlots, visitSlots } from "../../../lib/slots.js";
